import { Box, Flex, Icon, IconButton, Menu, MenuButton, MenuItem, MenuList, Button, Text, Image } from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import { getConfigFromStorage } from '@/utils/config';
import { useNavigate } from 'react-router';
import { formatDateToDateTimeDisplay } from '@/utils/formatDateToDateTimeDisplay';
import { CustomTable } from '@/components/CustomTable/CustomTable';
import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import {
  Challenge,
  ChallengeInteractivity,
  ChallengeStatus,
  colorByChallengeStatus,
  translateChallengeInteractivity,
  translateChallengeStatus,
} from '@/model';
import dayjs from '@/utils/dayjs.config';
import { formatInteger } from '@/utils/numberFormat';
import { useTranslation } from 'react-i18next';

export interface ChallengeTableProps {
  data: Challenge[];
  isLoading: boolean;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  totalItems: number;
  sorting?: { field: string; desc: boolean };
  onSortingChange?: (field: string, desc: boolean) => void;
  onPaginationChange: (page: number, pageSize: number) => void;
  onFinishClick?: (item: Challenge) => void;
  onEditClick: (item: Challenge) => void;
  onDeleteClick: (item: Challenge) => void;
}

export const ChallengeTable = ({
  data,
  isLoading,
  pageNumber,
  pageSize,
  totalPages,
  sorting,
  totalItems,
  onSortingChange,
  onPaginationChange,
  onFinishClick,
  onEditClick,
  onDeleteClick,
}: ChallengeTableProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const columnHelper = createColumnHelper<Challenge>();
  const isMultiTeam = getConfigFromStorage()?.is_multi_team ?? true;

  const columns = [
    columnHelper.accessor('created_at', {
      cell: props => formatDateToDateTimeDisplay(props.getValue()),
      header: t('pages.application_challenge.components.challenge_table.challenge_table_created_at_header'),
    }),
    columnHelper.accessor('team_name', {
      cell: props => {
        const { team_image, team_name } = props.row.original;
        return (
          <Flex alignItems='center' gap={2}>
            {team_image ? <Image src={team_image} alt={team_name || ''} w='24px' h='24px' objectFit='contain' /> : null}
            <Text fontWeight='bold'>{team_name || ''}</Text>
          </Flex>
        );
      },
      header: t('pages.application_challenge.components.challenge_table.challenge_table_team_header'),
    }),
    columnHelper.accessor('starts_in', {
      cell: props => formatDateToDateTimeDisplay(props.getValue()),
      header: t('pages.application_challenge.components.challenge_table.challenge_table_starts_in_header'),
    }),
    columnHelper.accessor('title', {
      cell: props => props.getValue(),
      header: t('pages.application_challenge.components.challenge_table.challenge_table_title_header'),
    }),
    columnHelper.accessor('total_participants', {
      cell: props => formatInteger(props.getValue()),
      header: t('pages.application_challenge.components.challenge_table.challenge_table_total_participants_header'),
    }),
    columnHelper.accessor('winner', {
      cell: props => {
        if (props.row.original.winner) {
          return props.row.original.winner?.display_name;
        }
        return t('pages.application_challenge.components.challenge_table.challenge_table_winner_empty');
      },
      header: t('pages.application_challenge.components.challenge_table.challenge_table_winner_header'),
    }),
    columnHelper.accessor('status', {
      cell: props => (
        <Text position='relative' paddingLeft='16px'>
          <Box
            as='span'
            position='absolute'
            top='50%'
            left='0'
            transform='translateY(-50%)'
            w='8px'
            h='8px'
            borderRadius='50%'
            bgColor={colorByChallengeStatus(props.getValue())}
            zIndex={9999}
            aria-hidden='true'
          />
          {translateChallengeStatus(props.getValue())}
        </Text>
      ),
      header: t('pages.application_challenge.components.challenge_table.challenge_table_status_header'),
    }),

    columnHelper.accessor('interactivity', {
      cell: props => {
        const startsIn = props.row.original.starts_in;
        const interactivity = props.getValue();
        if (interactivity === ChallengeInteractivity.ENABLED && startsIn && dayjs(startsIn).isAfter(dayjs())) {
          return t('pages.application_challenge.components.challenge_table.challenge_table_interactivity_not_started');
        }
        return translateChallengeInteractivity(interactivity);
      },
      header: t('pages.application_challenge.components.challenge_table.challenge_table_interactivity_header'),
    }),

    columnHelper.display({
      id: 'justify-expenses',
      header: '',
      cell: props => {
        const { status, interactivity } = props.row.original;

        // Oculta o botão se não estiver publicado ou se a interação estiver encerrada
        if (status !== ChallengeStatus.PUBLISHED || interactivity === ChallengeInteractivity.CLOSED) {
          return '';
        }

        return (
          <Button
            onClick={() => onFinishClick?.(props.row.original as Challenge)}
            variant='link'
            color='brand.500'
            textDecor='underline'
            display='flex'
            alignItems='center'
            gap={2}
          >
            {t('pages.application_challenge.components.challenge_table.challenge_table_finish_button')}
          </Button>
        );
      },
      enableSorting: false,
      meta: {
        align: 'left',
      },
    }),

    columnHelper.display({
      id: 'actions',
      header: t('pages.application_challenge.components.challenge_table.challenge_table_actions_header'),
      cell: props => (
        <Flex justifyContent={'end'}>
          <Menu>
            <MenuButton
              textColor='text.table'
              as={IconButton}
              variant='ghost'
              size='sm'
              icon={<Icon as={EllipsisVerticalIcon} />}
              aria-label={t('pages.application_challenge.components.challenge_table.challenge_table_more_actions_label')}
            />
            <MenuList textColor='white'>
              <MenuItem
                onClick={() => navigate(`/application/challenge/${props.row.original.uuid}`)}
                icon={
                  <Text>
                    <ChakraFeatherIcon name='eye' />
                  </Text>
                }
              >
                <Text>{t('pages.application_challenge.components.challenge_table.challenge_table_view_details_action')}</Text>
              </MenuItem>
              <MenuItem
                onClick={() => onDeleteClick?.(props.row.original as Challenge)}
                icon={
                  <Text>
                    <ChakraFeatherIcon name='trash-2' color='red.500' w='18px' />
                  </Text>
                }
              >
                <Text>{t('pages.application_challenge.components.challenge_table.challenge_table_delete_action')}</Text>
              </MenuItem>
            </MenuList>
          </Menu>
        </Flex>
      ),
      enableSorting: false,
      meta: {
        align: 'right',
      },
    }),
  ];

  return (
    <CustomTable
      data={data}
      isLoading={isLoading}
      pageNumber={pageNumber}
      pageSize={pageSize}
      totalPages={totalPages}
      totalItems={totalItems}
      columns={isMultiTeam ? columns : columns.filter(col => (col as any).accessorKey !== 'team_name')}
      onPaginationChange={onPaginationChange}
      sorting={sorting}
      onSortingChange={onSortingChange}
    />
  );
};
